import React, { useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DeviceMobile, Copy, ShareNetwork, CheckCircle, QrCode, ArrowUpRight } from "@phosphor-icons/react";

/**
 * Dashboard card that lets a desk-side safety officer hand SafeForm.ai
 * to a floor supervisor's phone in one step:
 *   - QR code of the current origin
 *   - Copy install link
 *   - Web Share API where available
 */
export default function InstallLinkCard() {
  const [copied, setCopied] = useState(false);

  const installUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.origin + "/dashboard/upload";
  }, []);

  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(installUrl);
      setCopied(true);
      toast.success("Install link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  const onShare = async () => {
    try {
      await navigator.share({
        title: "Install SafeForm.ai",
        text: "Record a task on a phone or camera. SafeForm analyzes posture and motion, flags ergonomic risk, and shows a safer technique.",
        url: installUrl,
      });
    } catch (e) {
      // user cancelled — silent
    }
  };

  if (!installUrl) return null;

  return (
    <div
      data-testid="install-link-card"
      className="border-2 border-ink-900 bg-white grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-0"
    >
      {/* QR */}
      <div className="bg-ink-900 p-6 flex items-center justify-center">
        <div className="bg-white p-3 border-4 border-safety">
          <QRCodeSVG
            value={installUrl}
            size={132}
            bgColor="#FFFFFF"
            fgColor="#09090B"
            level="M"
            includeMargin={false}
            imageSettings={{
              src: "/brand/safeform-192.png",
              height: 28,
              width: 28,
              excavate: true,
            }}
          />
        </div>
      </div>

      {/* Copy text + actions */}
      <div className="p-6 flex flex-col">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-safety flex items-center justify-center">
            <DeviceMobile size={14} weight="bold" className="text-white" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">Get it on every phone</div>
        </div>
        <h3 className="mt-3 font-display font-bold text-2xl tracking-tight text-ink-900 leading-tight">
          Hand SafeForm to your floor supervisors.
        </h3>
        <p className="mt-2 text-sm text-ink-700 leading-relaxed">
          Scan with any phone camera, or copy the link. The app installs to the home screen and opens straight to the upload screen, so supervisors can record on the floor and review the hazard report.
        </p>

        {/* URL pill + copy */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 min-w-0 px-3 py-2 bg-ink-50 border border-ink-100 font-mono text-xs text-ink-700 truncate" data-testid="install-link-url" title={installUrl}>
            {installUrl}
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="install-link-copy"
              onClick={onCopy}
              size="sm"
              className={`rounded-none ${copied ? "bg-hazard-safe" : "bg-ink-900 hover:bg-safety"} text-white h-9 px-3`}
            >
              {copied ? <CheckCircle size={14} weight="bold" className="mr-1.5" /> : <Copy size={14} weight="bold" className="mr-1.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            {canShare && (
              <Button
                data-testid="install-link-share"
                onClick={onShare}
                size="sm"
                variant="outline"
                className="rounded-none border-ink-100 hover:border-ink-900 h-9 px-3"
              >
                <ShareNetwork size={14} weight="bold" className="mr-1.5" />
                Share
              </Button>
            )}
            <a
              href={installUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="install-link-open"
              className="inline-flex items-center px-3 h-9 border border-ink-100 text-ink-700 hover:border-ink-900 text-sm"
            >
              <ArrowUpRight size={14} weight="bold" />
            </a>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[9px] uppercase tracking-[0.2em] text-ink-500">
          <Step n="01" label="Scan / open" icon={<QrCode size={12} weight="bold" />} />
          <Step n="02" label="Tap install" icon={<DeviceMobile size={12} weight="bold" />} />
          <Step n="03" label="Record on floor" icon={<CheckCircle size={12} weight="bold" />} />
        </div>
      </div>
    </div>
  );
}

function Step({ n, label, icon }) {
  return (
    <div className="border border-ink-100 px-2 py-1.5 flex items-center gap-2">
      <span className="text-safety">{icon}</span>
      <span className="text-ink-700"><span className="text-ink-300">{n}</span> · {label}</span>
    </div>
  );
}
